import { AD_ATTRIBUTION_STORAGE_KEY } from '@web-solutions/module-attribution';

// sessionStorage lives for the lifetime of the tab: it survives page reloads
// (F5) and the 3DS/bank challenge (which Stripe renders in an iframe overlay,
// without navigating the page away), but is cleared when the tab is actually
// closed. We use it as a "same session" marker — a missing marker on load means
// the previous tab was closed, so the persisted data is stale and we wipe both
// storages for a fresh funnel.
const SESSION_FLAG = 'onboarding-session-alive';

/**
 * Wipe localStorage and sessionStorage when the app is opened in a genuinely
 * new tab session, while keeping them intact within the same session.
 *
 * Payment uses the embedded card form: the 3DS challenge runs in an iframe and
 * never unloads the page, so the marker survives it and the data is preserved.
 *
 * Must run BEFORE the zustand store module is imported (it hydrates from
 * localStorage synchronously at import time) — hence this module is imported
 * for its side effect as the very first line of index.tsx.
 */
export function clearStaleStorageOnFreshSession(): void {
  try {
    if (!sessionStorage.getItem(SESSION_FLAG)) {
      // The FB ad labels must survive the wipe. The ad click happens once, and
      // by the time the user reaches this app again — landing → quiz opened in
      // a new tab, the funnel reopened from history, an in-app browser handing
      // off to Safari — the URL no longer carries the params, so a cleared
      // `ad_attribution` can never be re-captured and the purchase lands in
      // "(unattributed)". The wipe exists to reset the questionnaire, not the
      // campaign the user came from; the backend stamps first touch anyway.
      const adAttribution = localStorage.getItem(AD_ATTRIBUTION_STORAGE_KEY);

      localStorage.clear();
      sessionStorage.clear();

      if (adAttribution) {
        localStorage.setItem(AD_ATTRIBUTION_STORAGE_KEY, adAttribution);
      }
    }
    // Set the marker AFTER clearing — sessionStorage.clear() wipes it too.
    sessionStorage.setItem(SESSION_FLAG, '1');
  } catch (_) {
    /* ignore storage errors (private mode / storage disabled) */
  }
}

// Run immediately on import so it executes before the persisted store hydrates.
clearStaleStorageOnFreshSession();
